import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  rectIntersection,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { useNavigate } from "react-router-dom";
import { BlockCatalog } from "./BlockCatalog";
import { PageBuilderCanvas } from "./PageBuilderCanvas";
import { BlockEditor } from "./BlockEditor";
import { HtmlEditor } from "./HtmlEditor";
import { ResponsivePreview } from "./ResponsivePreview";
import { EnhancedBlockEditor } from "./EnhancedBlockEditor";
import { EnhancedResponsivePreview } from "./EnhancedResponsivePreview";
import { UIBlockData } from "./UIBlockRenderer";
import {
  useGetUIBlockByIdQuery,
  useUpdatePageBlocksMutation,
  useGeneratePagePreviewMutation,
  UIBlock,
  PageBlock,
} from "../../api/uiBlocksApi";
import {
  useGetPageByIdQuery,
  useCreatePageMutation,
  LandingPage,
} from "../../api/pagesApi";
import { useAppSelector } from "../../hooks/redux";

const BuilderContainer = styled.div`
  display: flex;
  height: 100vh;
  background: #f8f9fa;
  overflow: hidden;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1500;
  font-size: 16px;
  color: #6b7280;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: #f8f9fa;
  text-align: center;
  color: #ef4444;
  padding: 40px;

  h2 {
    margin: 0 0 16px 0;
    color: #1f2937;
  }
`;

interface CanvasBlock extends PageBlock {
  block: UIBlock & UIBlockData;
}

interface SlotItemData {
  id: string;
  content: string;
  type: "text" | "block";
  blockId?: string;
  blockData?: UIBlock;
  overrides?: Record<string, any>;
}

interface SlotData {
  id: string;
  name: string;
  displayName: string;
  items: SlotItemData[];
}

interface PageBuilderV2Props {
  pageId?: string;
  onBack?: () => void;
  onSave?: (page: LandingPage) => void;
}

export const PageBuilderV2: React.FC<PageBuilderV2Props> = ({
  pageId,
  onBack,
  onSave,
}) => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const isCreateMode = !pageId;

  // State management
  const [blocks, setBlocks] = useState<CanvasBlock[]>([]);
  const [pageTitle, setPageTitle] = useState("New Landing Page");
  const [selectedBlock, setSelectedBlock] = useState<CanvasBlock | null>(null);
  const [isBlockEditorOpen, setIsBlockEditorOpen] = useState(false);
  const [isEnhancedEditorOpen, setIsEnhancedEditorOpen] = useState(false);
  const [isHtmlEditorOpen, setIsHtmlEditorOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isEnhancedPreviewOpen, setIsEnhancedPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [blockSlots, setBlockSlots] = useState<
    Record<string, Record<string, SlotData>>
  >({});
  const [isSaving, setIsSaving] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeDragData, setActiveDragData] = useState<any>(null);

  // DnD sensors
  const sensors = useSensors(useSensor(PointerSensor));

  // API hooks
  const {
    data: pageData,
    isLoading: pageLoading,
    error: pageError,
  } = useGetPageByIdQuery(
    { id: pageId!, includeConfig: true },
    { skip: isCreateMode }
  );

  const { data: selectedBlockData } = useGetUIBlockByIdQuery(
    selectedBlock?.blockId || "",
    { skip: !selectedBlock?.blockId }
  );

  const [updatePageBlocks] = useUpdatePageBlocksMutation();
  const [generatePreview] = useGeneratePagePreviewMutation();
  const [createPage] = useCreatePageMutation();

  // Load existing page data
  useEffect(() => {
    if (pageData && !isCreateMode) {
      setPageTitle(pageData.title);
      // TODO: Load existing blocks from pageData
      // This would require the API to return block configuration
      setBlocks([]);
    }
  }, [pageData, isCreateMode]);

  const handleBlocksChange = (newBlocks: CanvasBlock[]) => {
    setBlocks(newBlocks);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    setActiveDragData(event.active.data.current);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveDragData(null);

    if (!over) return;

    // Handle dropping a new block from catalog
    if (
      active.data.current?.type === "ui-block" &&
      over.id === "canvas-droppable"
    ) {
      const uiBlock = active.data.current.block as UIBlock;

      // Initialize overrides with schema default values
      const defaultOverrides: Record<string, any> = {};
      Object.entries(uiBlock.schema.editableFields || {}).forEach(
        ([key, defaultValue]) => {
          defaultOverrides[key] = defaultValue;
        }
      );

      const newCanvasBlock: CanvasBlock = {
        id: `canvas-block-${Date.now()}`,
        blockId: uiBlock.id,
        position: blocks.length,
        overrides: defaultOverrides,
        block: uiBlock,
      };
      setBlocks([...blocks, newCanvasBlock]);
      return;
    }

    // Handle reordering existing blocks
    if (
      active.data.current?.type === "canvas-block" &&
      over.data.current?.type === "canvas-block"
    ) {
      const activeIndex = blocks.findIndex((block) => block.id === active.id);
      const overIndex = blocks.findIndex((block) => block.id === over.id);

      if (activeIndex !== overIndex) {
        const reorderedBlocks = arrayMove(blocks, activeIndex, overIndex);
        // Update positions
        const updatedBlocks = reorderedBlocks.map((block, index) => ({
          ...block,
          position: index,
        }));
        setBlocks(updatedBlocks);
      }
    }
  };

  const handleBlockEdit = (blockId: string, useEnhanced = true) => {
    const block = blocks.find((b) => b.id === blockId);
    if (block) {
      setSelectedBlock(block);
      if (useEnhanced) {
        setIsEnhancedEditorOpen(true);
      } else {
        setIsBlockEditorOpen(true);
      }
    }
  };

  const handleBlockSave = (
    overrides: Record<string, any>,
    customHtml?: string
  ) => {
    if (selectedBlock) {
      const updatedBlocks = blocks.map((block) =>
        block.id === selectedBlock.id
          ? { ...block, overrides, customHtml }
          : block
      );
      setBlocks(updatedBlocks);
      setSelectedBlock(null);
    }
  };

  const handleEnhancedBlockSave = (
    overrides: Record<string, any>,
    slots: Record<string, SlotData>
  ) => {
    if (selectedBlock) {
      const updatedBlocks = blocks.map((block) =>
        block.id === selectedBlock.id ? { ...block, overrides } : block
      );
      setBlocks(updatedBlocks);

      // Update slots for this block
      setBlockSlots((prev) => ({
        ...prev,
        [selectedBlock.id]: slots,
      }));

      setSelectedBlock(null);
    }
  };

  const handleAddSlotItem = (slotName: string, type: "text" | "block") => {
    // This would be called from the enhanced editor to add new slot items
    console.log("Adding slot item:", slotName, type);
  };

  const handleHtmlEdit = () => {
    setIsBlockEditorOpen(false);
    setIsHtmlEditorOpen(true);
  };

  const handleHtmlSave = (html: string) => {
    if (selectedBlock) {
      const updatedBlocks = blocks.map((block) =>
        block.id === selectedBlock.id ? { ...block, customHtml: html } : block
      );
      setBlocks(updatedBlocks);
      setSelectedBlock(null);
    }
  };

  const handleSave = async () => {
    // Validate title
    if (!pageTitle || pageTitle.trim() === "") {
      alert("Please enter a page title before saving.");
      return;
    }

    setIsSaving(true);
    try {
      if (isCreateMode) {
        // Create new page
        const createPageRequest = {
          title: pageTitle.trim(),
          slug: pageTitle
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "-")
            .replace(/[^a-z0-9-]/g, ""),
          template: "page-builder-v2",
          customerId: user?.customerId || "",
          config: {},
        };

        console.log("Creating page with data:", createPageRequest);
        console.log("User data:", user);
        console.log("Page title before trim:", JSON.stringify(pageTitle));
        console.log("Page title after trim:", JSON.stringify(pageTitle.trim()));

        const pageData = await createPage(createPageRequest).unwrap();

        // Update with blocks
        await updatePageBlocks({
          pageId: pageData.id,
          data: {
            blocks: blocks.map((block) => ({
              blockId: block.blockId,
              overrides: block.overrides,
              customHtml: block.customHtml,
            })),
          },
        }).unwrap();

        if (onSave) {
          onSave(pageData);
        } else {
          navigate("/dashboard/pages");
        }
      } else {
        // Update existing page
        await updatePageBlocks({
          pageId: pageId!,
          data: {
            blocks: blocks.map((block) => ({
              blockId: block.blockId,
              overrides: block.overrides,
              customHtml: block.customHtml,
            })),
          },
        }).unwrap();

        if (onSave && pageData) {
          onSave(pageData);
        }
      }
    } catch (error: any) {
      console.error("Failed to save page:", error);
      console.error("Error details:", {
        message: error.message,
        data: error.data,
        status: error.status,
        originalStatus: error.originalStatus,
      });

      if (error.data?.error) {
        alert(`Failed to save page: ${error.data.error}`);
      } else {
        alert("Failed to save page. Please try again.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = async (useEnhanced = true) => {
    // Validate title for preview
    if (!pageTitle || pageTitle.trim() === "") {
      alert("Please enter a page title before previewing.");
      return;
    }

    if (useEnhanced) {
      // Use frontend rendering for enhanced preview
      setIsEnhancedPreviewOpen(true);
    } else {
      // Use backend API for traditional preview
      try {
        const result = await generatePreview({
          title: pageTitle.trim(),
          blocks: blocks.map((block) => ({
            blockId: block.blockId,
            overrides: block.overrides,
            customHtml: block.customHtml,
          })),
        }).unwrap();

        setPreviewHtml(result.htmlContent);
        setIsPreviewOpen(true);
      } catch (error) {
        console.error("Failed to generate preview:", error);
        alert("Failed to generate preview. Please try again.");
      }
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate("/dashboard/pages");
    }
  };

  if (!isCreateMode && pageLoading) {
    return <LoadingOverlay>Loading page builder...</LoadingOverlay>;
  }

  if (!isCreateMode && pageError) {
    return (
      <ErrorContainer>
        <div>
          <h2>Failed to Load Page</h2>
          <p>Could not load the page builder. Please try again.</p>
          <button onClick={handleBack}>Back to Pages</button>
        </div>
      </ErrorContainer>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={rectIntersection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <BuilderContainer>
        <BlockCatalog />

        <PageBuilderCanvas
          pageTitle={pageTitle}
          blocks={blocks}
          onBlocksChange={handleBlocksChange}
          onBlockEdit={(blockId) => handleBlockEdit(blockId, true)}
          onSave={handleSave}
          onPreview={() => handlePreview(true)}
          onTitleChange={setPageTitle}
          isSaving={isSaving}
          activeId={activeId}
          activeDragData={activeDragData}
        />

        <BlockEditor
          block={selectedBlockData || null}
          overrides={selectedBlock?.overrides || {}}
          customHtml={selectedBlock?.customHtml}
          isOpen={isBlockEditorOpen}
          onClose={() => {
            setIsBlockEditorOpen(false);
            setSelectedBlock(null);
          }}
          onSave={handleBlockSave}
          onHtmlEdit={handleHtmlEdit}
        />

        <EnhancedBlockEditor
          block={(selectedBlockData as UIBlockData) || null}
          overrides={selectedBlock?.overrides || {}}
          slots={selectedBlock ? blockSlots[selectedBlock.id] || {} : {}}
          isOpen={isEnhancedEditorOpen}
          onClose={() => {
            setIsEnhancedEditorOpen(false);
            setSelectedBlock(null);
          }}
          onSave={handleEnhancedBlockSave}
          onAddSlotItem={handleAddSlotItem}
        />

        <HtmlEditor
          isOpen={isHtmlEditorOpen}
          blockName={selectedBlockData?.name || ""}
          blockCategory={selectedBlockData?.category || ""}
          initialHtml={
            selectedBlock?.customHtml || selectedBlockData?.htmlContent || ""
          }
          originalHtml={selectedBlockData?.htmlContent || ""}
          onSave={handleHtmlSave}
          onClose={() => {
            setIsHtmlEditorOpen(false);
            setSelectedBlock(null);
          }}
        />

        <ResponsivePreview
          isOpen={isPreviewOpen}
          pageTitle={pageTitle}
          htmlContent={previewHtml}
          onClose={() => setIsPreviewOpen(false)}
          onRefresh={() => handlePreview(false)}
        />

        <EnhancedResponsivePreview
          isOpen={isEnhancedPreviewOpen}
          pageTitle={pageTitle}
          blocks={blocks.map((block) => {
            console.log("Mapping block for preview:", {
              id: block.id,
              blockId: block.blockId,
              blockName: block.block.name,
              overrides: block.overrides,
              position: block.position,
              blockSlots: blockSlots[block.id],
              hasSlotData: !!blockSlots[block.id],
            });

            return {
              id: block.id,
              blockId: block.blockId,
              overrides: block.overrides,
              position: block.position,
              block: block.block as UIBlockData,
              slotData: blockSlots[block.id] || {},
            };
          })}
          onClose={() => setIsEnhancedPreviewOpen(false)}
          onRefresh={() => handlePreview(true)}
        />

        {isSaving && <LoadingOverlay>Saving page...</LoadingOverlay>}
      </BuilderContainer>
    </DndContext>
  );
};
